import { AxiosInstance } from 'axios';
import {
    ICommunityPost,
    ICommunityComment,
    ICreateCommentResponse,
    IReactionSummary,
    ICommunity,
    ICommunityChannel,
    ICreateCommunityPostRequest,
    IReportContentRequest,
    IAttachableCommunityEvent,
    CommunityAttachEventScope,
    ICommunityDirectoryMember,
    ICommunityFeedResponse,
    HomeFeedItemType,
    HomeFeedItemTypeValue,
    ILinkPreview,
    ICollaboratorOrganisation,
    IHomeFeedPagedResponse,
    IHomeFeedCountsMetadata,
    IHomeFeedItem,
    ICreateUserPostRequest,
    IUserPost,
    IModerationCase,
    IStagedImageResult,
} from '../interfaces/ICommunityHub';
import { IPagedFilterResponse } from '../interfaces/IPagedFilterResponse';
import { isProtectedCommunityImageRoute } from '../utils/communityImageUtils';

const homeFeedItemTypeMap: Record<HomeFeedItemTypeValue, HomeFeedItemType> = {
    [HomeFeedItemTypeValue.Event]: 'Event',
    [HomeFeedItemTypeValue.CommunityPost]: 'CommunityPost',
    [HomeFeedItemTypeValue.UserPost]: 'UserPost',
};

function normaliseHomeFeedItemType(itemType: unknown, item: Partial<IHomeFeedItem>): HomeFeedItemType {
    if (itemType === 'Event' || itemType === 'CommunityPost' || itemType === 'UserPost') {
        return itemType;
    }

    if (typeof itemType === 'number' && itemType in homeFeedItemTypeMap) {
        return homeFeedItemTypeMap[itemType as HomeFeedItemTypeValue];
    }

    if (item.userPost) {
        return 'UserPost';
    }

    if (item.post) {
        return 'CommunityPost';
    }

    return 'Event';
}

function normaliseHomeFeedItem(item: Partial<IHomeFeedItem>): IHomeFeedItem {
    const source = item as Record<string, unknown>;
    const rawScope = source.scope;
    const rawPodId = source.pod_id ?? source.podId;
    const rawPodName = source.pod_name ?? source.podName;
    const rawPodGradient = source.pod_gradient ?? source.podGradient;

    const userPost = item.userPost
        ? {
            ...item.userPost,
            scope: typeof item.userPost.scope === 'string'
                ? item.userPost.scope
                : typeof rawScope === 'string'
                    ? rawScope
                    : undefined,
            pod_id: typeof item.userPost.pod_id === 'string'
                ? item.userPost.pod_id
                : typeof rawPodId === 'string'
                    ? rawPodId
                    : undefined,
            pod_name: typeof item.userPost.pod_name === 'string'
                ? item.userPost.pod_name
                : typeof rawPodName === 'string'
                    ? rawPodName
                    : undefined,
            pod_gradient: typeof item.userPost.pod_gradient === 'string'
                ? item.userPost.pod_gradient
                : typeof rawPodGradient === 'string'
                    ? rawPodGradient
                    : undefined,
        }
        : item.userPost;

    return {
        ...item,
        userPost,
        itemType: normaliseHomeFeedItemType(item.itemType, item),
        timestamp: typeof item.timestamp === 'string' ? item.timestamp : '',
    } as IHomeFeedItem;
}

function getHttpStatus(error: unknown): number | undefined {
    if (typeof error !== 'object' || error === null) {
        return undefined;
    }

    const response = (error as { response?: { status?: unknown } }).response;
    return typeof response?.status === 'number' ? response.status : undefined;
}

interface CommentRequestOptions {
    includeReactions?: boolean;
    signal?: AbortSignal;
}

function commentRequestConfig(page: number, pageSize: number, options?: CommentRequestOptions) {
    const params = { page, pageSize, ...(options?.includeReactions ? { includeReactions: true } : {}) };
    return options?.signal ? { params, signal: options.signal } : { params };
}

/**
 * Service for participant-side Community Hub API interactions
 * Calls /participant/community-hub endpoints
 */
export const PARTICIPANT_CHANNELS_PAGE_SIZE = 50;

export class ParticipantCommunityHubService {
    private readonly baseUrl = '/participant/community-hub';
    private readonly homeFeedUrl = '/participant/home-feed';
    private readonly apiClient: AxiosInstance;
    private readonly pendingCommunityImages = new Map<string, Promise<Blob | null>>();

    constructor(apiClient: AxiosInstance) {
        this.apiClient = apiClient;
    }

    /**
     * Explore public communities available to join
     */
    async explorePublicCommunities(
        page = 1,
        pageSize = 10,
        searchQuery?: string,
        options?: {
            membership?: 'all' | 'joined' | 'notJoined';
            visibility?: 'all' | 'Public' | 'Private' | 'Unlisted';
            sortBy?: 'name' | 'members';
            size?: 'all' | 'under100' | '100to1000' | '1000plus';
        },
        signal?: AbortSignal,
    ): Promise<{ communities: ICommunity[]; totalCount: number }> {
        const response = await this.apiClient.get(`${this.baseUrl}/explore`, {
            params: {
                page,
                pageSize,
                search: searchQuery?.trim() ? searchQuery.trim() : undefined,
                membership: options?.membership,
                visibility: options?.visibility,
                sortBy: options?.sortBy,
                size: options?.size,
            },
            ...(signal ? { signal } : {}),
        });
        return response.data;
    }

    /**
     * Get communities the participant has joined
     */
    async getJoinedCommunities(): Promise<ICommunity[]> {
        const response = await this.apiClient.get(`${this.baseUrl}/my-communities`);
        return response.data;
    }

    /**
     * Search the communities the participant has joined with server-side paging.
     */
    async getJoinedCommunitiesPage(
        page = 1,
        pageSize = 10,
        searchQuery?: string,
        options?: {
            allowAttendeePostsOnly?: boolean;
        },
    ): Promise<IPagedFilterResponse<ICommunity>> {
        const response = await this.apiClient.get(`${this.baseUrl}/my-communities/paged`, {
            params: {
                page,
                pageSize,
                search: searchQuery?.trim() ? searchQuery.trim() : undefined,
                allowAttendeePostsOnly: options?.allowAttendeePostsOnly,
            },
        });

        return {
            items: Array.isArray(response.data?.items) ? response.data.items as ICommunity[] : [],
            totalCount: typeof response.data?.totalCount === 'number' ? response.data.totalCount : 0,
            page: typeof response.data?.page === 'number' ? response.data.page : page,
            pageSize: typeof response.data?.pageSize === 'number' ? response.data.pageSize : pageSize,
        };
    }

    /**
     * Resolve a visible community by slug.
     */
    async getCommunityBySlug(slug: string): Promise<ICommunity> {
        const response = await this.apiClient.get(`${this.baseUrl}/by-slug/${encodeURIComponent(slug)}`);
        return response.data;
    }

    /**
     * Join a public community
     */
    async getChannels(communityId: string, page?: number, channelId?: string): Promise<ICommunityChannel[]> {
        const url = `${this.baseUrl}/${communityId}/channels`;
        const response = page !== undefined || channelId
            ? await this.apiClient.get(url, { params: { page: page ?? 1, ...(channelId ? { channelId } : {}) } })
            : await this.apiClient.get(url);
        return response.data;
    }

    async requestChannelAccess(communityId: string, channelId: string, message: string): Promise<void> {
        await this.apiClient.post(`${this.baseUrl}/${communityId}/channels/${channelId}/join`, { message });
    }

    async leaveChannel(communityId: string, channelId: string): Promise<void> {
        await this.apiClient.delete(`${this.baseUrl}/${communityId}/channels/${channelId}/membership`);
    }

    async muteChannel(communityId: string, channelId: string, isMuted: boolean): Promise<{ isMuted: boolean }> {
        const response = await this.apiClient.patch(`${this.baseUrl}/${communityId}/channels/${channelId}/mute`, { isMuted });
        return response.data;
    }

    async muteCommunity(communityId: string, isMuted: boolean): Promise<{ isMuted: boolean }> {
        const response = await this.apiClient.patch(`${this.baseUrl}/${communityId}/mute`, { isMuted });
        return response.data;
    }

    async joinCommunity(communityId: string): Promise<{ status: string }> {
        const response = await this.apiClient.post(`${this.baseUrl}/${communityId}/join`);
        return response.data;
    }

    /**
     * Leave a community
     */
    async leaveCommunity(communityId: string): Promise<{ status: string }> {
        const response = await this.apiClient.post(`${this.baseUrl}/${communityId}/leave`);
        return response.data;
    }

    /**
     * Get feed posts from joined communities
     */
    async getFeed(page = 1, pageSize = 10): Promise<ICommunityFeedResponse> {
        const response = await this.apiClient.get(`${this.baseUrl}/feed`, {
            params: { page, pageSize },
        });

        return {
            posts: Array.isArray(response.data?.posts) ? response.data.posts as ICommunityPost[] : [],
            totalCount: typeof response.data?.totalCount === 'number' ? response.data.totalCount : 0,
            summary: {
                recentPostsCount: typeof response.data?.summary?.recentPostsCount === 'number' ? response.data.summary.recentPostsCount : 0,
                openQuestionsCount: typeof response.data?.summary?.openQuestionsCount === 'number' ? response.data.summary.openQuestionsCount : 0,
                livePollsCount: typeof response.data?.summary?.livePollsCount === 'number' ? response.data.summary.livePollsCount : 0,
            },
        };
    }

    async getLinkPreview(url: string): Promise<ILinkPreview> {
        const response = await this.apiClient.get(`${this.baseUrl}/link-preview`, {
            params: { url },
        });
        return response.data;
    }

    /**
     * Get unified personal home feed: events, community posts, and personal posts.
     *
     * `filter` accepts the same values as the mobile app's `HomeFeedFilter`:
     * - 'author': all posts (personal + community) authored by the current user - "My Posts".
     * - 'mypersonal' / 'mycommunity': author-scoped to just one post source.
     * Pass `includeCounts` to also receive an accurate `counts` breakdown (page 1 only, author-scoped
     * filters only) and `countsOnly` to skip building the paged item list entirely - the fastest way
     * to fetch just a stat number. Request event reaction summaries only for views that display them.
     */
    async getHomeFeed(
        page = 1,
        pageSize = 20,
        filter: 'all' | 'events' | 'community' | 'people' | 'author' | 'mypersonal' | 'mycommunity' = 'all',
        includeCounts = false,
        countsOnly = false,
        options?: { includeEventReactions?: boolean },
    ): Promise<IHomeFeedPagedResponse> {
        const response = await this.apiClient.get(this.homeFeedUrl, {
            params: { page, pageSize, filter, includeCounts, countsOnly, ...(options?.includeEventReactions ? { includeEventReactions: true } : {}) },
        });

        const rawCounts = response.data?.counts;
        const counts: IHomeFeedCountsMetadata | undefined = rawCounts ? {
            total: typeof rawCounts.total === 'number' ? rawCounts.total : 0,
            personal: typeof rawCounts.personal === 'number' ? rawCounts.personal : 0,
            communities: typeof rawCounts.communities === 'number' ? rawCounts.communities : 0,
        } : undefined;

        return {
            items: Array.isArray(response.data?.items)
                ? response.data.items.map((item: Partial<IHomeFeedItem>) => normaliseHomeFeedItem(item))
                : [],
            totalCount: typeof response.data?.totalCount === 'number' ? response.data.totalCount : 0,
            page: typeof response.data?.page === 'number' ? response.data.page : page,
            pageSize: typeof response.data?.pageSize === 'number' ? response.data.pageSize : pageSize,
            hasMore: typeof response.data?.hasMore === 'boolean' ? response.data.hasMore : false,
            counts,
        };
    }

    /**
     * Publish a post to the user's personal feed.
     */
    async createPersonalPost(request: ICreateUserPostRequest): Promise<IUserPost> {
        const response = await this.apiClient.post(`${this.homeFeedUrl}/posts`, request);
        return response.data as IUserPost;
    }

    /**
     * Upload an image to a personal feed post.
     */
    async uploadPersonalPostImage(postId: string, file: File): Promise<IUserPost> {
        const formData = new FormData();
        formData.append('image', file);
        const response = await this.apiClient.post(`${this.homeFeedUrl}/posts/${postId}/image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data as IUserPost;
    }

    /**
     * Get posts from a specific community
     */
    async getCommunityPosts(communityId: string, page = 1, pageSize = 10, channelId?: string): Promise<ICommunityPost[]> {
        const response = await this.apiClient.get(`${this.baseUrl}/${communityId}/feed`, {
            params: { page, pageSize, ...(channelId ? { channelId } : {}) },
        });

        // Backward-compatible normalisation: endpoint may return either an array or { posts }.
        if (Array.isArray(response.data)) {
            return response.data as ICommunityPost[];
        }

        return (response.data?.posts ?? []) as ICommunityPost[];
    }

    /**
     * Get a paged directory of visible community members.
     */
    async getCommunityMembersPage(communityId: string, page = 1, pageSize = 25, search?: string): Promise<IPagedFilterResponse<ICommunityDirectoryMember>> {
        const response = await this.apiClient.get(`${this.baseUrl}/${communityId}/members/paged`, {
            params: { page, pageSize, ...(search?.trim() ? { search: search.trim() } : {}) },
        });

        return {
            items: Array.isArray(response.data?.items) ? response.data.items as ICommunityDirectoryMember[] : [],
            totalCount: typeof response.data?.totalCount === 'number' ? response.data.totalCount : 0,
            page: typeof response.data?.page === 'number' ? response.data.page : page,
            pageSize: typeof response.data?.pageSize === 'number' ? response.data.pageSize : pageSize,
        };
    }

    async getCommunityCollaborators(communityId: string): Promise<ICollaboratorOrganisation[]> {
        const response = await this.apiClient.get(`${this.baseUrl}/${communityId}/collaborators`);
        return Array.isArray(response.data) ? response.data as ICollaboratorOrganisation[] : [];
    }

    /**
     * Get community-scoped events that are valid to attach to a post.
     */
    async getAttachableEvents(
        communityId: string,
        scope: CommunityAttachEventScope = 'my',
        query?: string,
        page = 1,
        pageSize = 6,
        upcomingOnly = true,
    ): Promise<IPagedFilterResponse<IAttachableCommunityEvent>> {
        const response = await this.apiClient.get(`${this.baseUrl}/${communityId}/attachable-events`, {
            params: {
                scope,
                query: query?.trim() || undefined,
                page,
                pageSize,
                upcomingOnly,
            },
        });

        return {
            items: Array.isArray(response.data?.items) ? (response.data.items as IAttachableCommunityEvent[]) : [],
            totalCount: typeof response.data?.totalCount === 'number' ? response.data.totalCount : 0,
            page: typeof response.data?.page === 'number' ? response.data.page : page,
            pageSize: typeof response.data?.pageSize === 'number' ? response.data.pageSize : pageSize,
        };
    }

    async getEventShortUrl(eventId: string): Promise<string | null> {
        const response = await fetch(`/e/short-url/${eventId}`);
        if (!response.ok) {
            return null;
        }

        const data = (await response.json()) as { shortUrl?: string };
        return data.shortUrl ?? null;
    }

    /**
     * Create a post in a joined community
     */
    async createPost(communityId: string, request: ICreateCommunityPostRequest): Promise<ICommunityPost> {
        const response = await this.apiClient.post(`${this.baseUrl}/${communityId}/posts`, request);
        return response.data;
    }

    /**
     * Cast or toggle a vote on a poll option.
     */
    async votePollOption(postId: string, optionId: string): Promise<ICommunityPost> {
        const response = await this.apiClient.post(`${this.baseUrl}/posts/${postId}/poll/vote`, { optionId });
        return response.data;
    }

    /**
     * Create a comment on a post
     * Automatically detects whether it's a community post or personal post.
     */
    async createComment(postId: string, body: string, isPersonalPost?: boolean): Promise<ICreateCommentResponse> {
        // If caller explicitly specifies post type, use that
        if (isPersonalPost === true) {
            const comment = await this.createPersonalPostComment(postId, body);
            const { totalCount } = await this.getPersonalPostComments(postId, 1, 1);
            return {
                comment,
                commentsCount: totalCount,
            };
        }

        if (isPersonalPost === false) {
            const response = await this.apiClient.post(`${this.baseUrl}/posts/${postId}/comments`, { body });
            return response.data;
        }

        // Auto-detect: try community endpoint first, fall back to personal if 404
        try {
            const response = await this.apiClient.post(`${this.baseUrl}/posts/${postId}/comments`, { body });
            return response.data;
        } catch (error) {
            // If 404, try personal post endpoint
            if (getHttpStatus(error) === 404) {
                const comment = await this.createPersonalPostComment(postId, body);
                const { totalCount } = await this.getPersonalPostComments(postId, 1, 1);
                return {
                    comment,
                    commentsCount: totalCount,
                };
            }
            throw error;
        }
    }

    /**
     * Get paginated comments for a post.
     * Automatically detects whether it's a community post or personal post.
     */
    async getPostComments(postId: string, page = 1, pageSize = 20, isPersonalPost?: boolean, options?: CommentRequestOptions): Promise<{ comments: ICommunityComment[]; totalCount: number }> {
        // If caller explicitly specifies post type, use that
        if (isPersonalPost === true) {
            return this.getPersonalPostComments(postId, page, pageSize, options);
        }

        if (isPersonalPost === false) {
            const response = await this.apiClient.get(`${this.baseUrl}/posts/${postId}/comments`, commentRequestConfig(page, pageSize, options));

            return {
                comments: response.data?.comments ?? [],
                totalCount: response.data?.totalCount ?? 0,
            };
        }

        // Auto-detect: try community endpoint first, fall back to personal if 404
        try {
            const response = await this.apiClient.get(`${this.baseUrl}/posts/${postId}/comments`, commentRequestConfig(page, pageSize, options));

            return {
                comments: response.data?.comments ?? [],
                totalCount: response.data?.totalCount ?? 0,
            };
        } catch (error) {
            // If 404, try personal post endpoint
            if (getHttpStatus(error) === 404) {
                return this.getPersonalPostComments(postId, page, pageSize, options);
            }
            throw error;
        }
    }

    /**
     * Delete a post as participant (author/moderator only).
     */
    async deletePost(communityId: string, postId: string): Promise<void> {
        await this.apiClient.delete(`${this.baseUrl}/${communityId}/posts/${postId}`);
    }

    /**
     * Delete a comment as participant (author/moderator only).
     */
    async deleteComment(communityId: string, postId: string, commentId: string): Promise<void> {
        await this.apiClient.delete(`${this.baseUrl}/${communityId}/posts/${postId}/comments/${commentId}`);
    }

    /**
     * Upload an image to attach to a post
     */
    async uploadPostImage(postId: string, file: File): Promise<ICommunityPost> {
        const formData = new FormData();
        formData.append('image', file);
        const response = await this.apiClient.post(`${this.baseUrl}/posts/${postId}/image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    }

    /**
     * Pre-upload a community post image through content safety before creating the post.
     * Returns the staged blob URL to include in the CreatePost request body.
     * Throws with the server's rejection message if the image fails the safety check.
     */
    async stageCommunityPostImage(communityId: string, file: File): Promise<string> {
        const formData = new FormData();
        formData.append('image', file);
        const response = await this.apiClient.post(`${this.baseUrl}/${communityId}/posts/stage-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return (response.data as { stagedUrl: string }).stagedUrl;
    }

    /**
     * Pre-upload a personal post image through content safety before creating the post.
     * Returns the staged blob URL to include in the CreatePersonalPost request body.
     * Throws with the server's rejection message if the image fails the safety check.
     */
    async stagePersonalPostImage(file: File): Promise<string> {
        const formData = new FormData();
        formData.append('image', file);
        const response = await this.apiClient.post(`${this.homeFeedUrl}/posts/stage-image`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return (response.data as { stagedUrl: string }).stagedUrl;
    }

    /**
     * Pre-upload up to 10 community post images through content safety before creating the post.
     * Each file is screened independently; a failure for one does not block the others.
     */
    async stageCommunityPostImages(communityId: string, files: File[]): Promise<IStagedImageResult[]> {
        const formData = new FormData();
        files.forEach((file) => formData.append('images', file));
        const response = await this.apiClient.post(`${this.baseUrl}/${communityId}/posts/stage-images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data as IStagedImageResult[];
    }

    /**
     * Pre-upload up to 10 personal post images through content safety before creating the post.
     * Each file is screened independently; a failure for one does not block the others.
     */
    async stagePersonalPostImages(files: File[]): Promise<IStagedImageResult[]> {
        const formData = new FormData();
        files.forEach((file) => formData.append('images', file));
        const response = await this.apiClient.post(`${this.homeFeedUrl}/posts/stage-images`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data as IStagedImageResult[];
    }

    /**
     * Resolve a channel logo image blob for participant viewing.
     */
    async getResolvedChannelLogoUrl(communityId: string, channelId: string): Promise<string | null> {
        try {
            const response = await this.apiClient.get<Blob>(`${this.baseUrl}/${communityId}/channels/${channelId}/logo/content`, {
                responseType: 'blob',
                headers: { 'Cache-Control': 'no-cache' },
            });
            return response.data.size > 0 ? URL.createObjectURL(response.data) : null;
        } catch {
            return null;
        }
    }

    /**
     * Resolve any community/channel logo or image URL already returned by the API for direct use in an
     * <img> src — membership-gated proxy routes need a blob-fetch first, anonymous/public ones can be used as-is.
     * Caller is responsible for revoking the returned blob URL (via URL.revokeObjectURL) on cleanup.
     */
    async getResolvedImageUrl(rawUrl: string | null | undefined): Promise<string | null> {
        if (!rawUrl) return null;
        if (!isProtectedCommunityImageRoute(rawUrl)) return rawUrl;

        let request = this.pendingCommunityImages.get(rawUrl);
        if (!request) {
            request = this.apiClient.get<Blob>(rawUrl, { responseType: 'blob' })
                .then(response => response.data)
                .catch((error: unknown) => {
                    console.warn('Could not load a community image.', error);
                    return null;
                })
                .finally(() => this.pendingCommunityImages.delete(rawUrl));
            this.pendingCommunityImages.set(rawUrl, request);
        }

        try {
            const image = await request;
            return image && image.size > 0 ? URL.createObjectURL(image) : null;
        } catch (error) {
            console.warn('Could not prepare a community image.', error);
            return null;
        }
    }

    /**
     * Resolve a fresh public image URL from a community post image id.
     */
    async getResolvedPostImageUrl(imageId: string): Promise<string> {
        try {
            const blobResponse = await this.apiClient.get<Blob>(`${this.baseUrl}/images/${imageId}/content`, {
                responseType: 'blob',
            });
            return URL.createObjectURL(blobResponse.data);
        } catch {
            // Fall back to URL endpoint if blob endpoint is unavailable.
        }

        const response = await this.apiClient.get<string | { url?: string; imageUrl?: string; value?: string }>(`${this.baseUrl}/images/${imageId}/url`);
        const payload = response.data;

        if (typeof payload === 'string') {
            return payload;
        }

        const resolvedUrl = payload?.imageUrl ?? payload?.url ?? payload?.value;
        if (typeof resolvedUrl === 'string' && resolvedUrl.trim().length > 0) {
            return resolvedUrl;
        }

        throw new Error('Unable to resolve post image URL.');
    }

    /**
     * Resolve a fresh public image URL for a specific image on a multi-image community post.
     */
    async getResolvedPostImageUrlById(postId: string, imageId: string): Promise<string> {
        try {
            const blobResponse = await this.apiClient.get<Blob>(`${this.baseUrl}/posts/${postId}/images/${imageId}/content`, {
                responseType: 'blob',
            });
            return URL.createObjectURL(blobResponse.data);
        } catch {
            // Fall back to URL endpoint if blob endpoint is unavailable.
        }

        const response = await this.apiClient.get<string | { url?: string; imageUrl?: string; value?: string }>(`${this.baseUrl}/posts/${postId}/images/${imageId}/url`);
        const payload = response.data;

        if (typeof payload === 'string') {
            return payload;
        }

        const resolvedUrl = payload?.imageUrl ?? payload?.url ?? payload?.value;
        if (typeof resolvedUrl === 'string' && resolvedUrl.trim().length > 0) {
            return resolvedUrl;
        }

        throw new Error('Unable to resolve post image URL.');
    }

    /**
     * Resolve every image on a multi-image community post in parallel, skipping any that fail
     * individually rather than failing the whole gallery.
     */
    async getResolvedPostImageUrls(postId: string, images: { id: string }[]): Promise<(string | null)[]> {
        const results = await Promise.allSettled(images.map((image) => this.getResolvedPostImageUrlById(postId, image.id)));
        return results.map((result) => (result.status === 'fulfilled' ? result.value : null));
    }

    /**
     * Resolve an event image URL using authenticated request first, then fallback URL.
     */
    async getResolvedEventImageUrl(eventId: string, fallbackImageUrl?: string | null, refresh = false): Promise<string | null> {
        const fallback = typeof fallbackImageUrl === 'string' ? fallbackImageUrl.trim() : '';
        if (!fallback) return null;
        if (!eventId?.trim()) return fallback;

        const response = await this.apiClient.get<Blob>(`/event/${eventId}/image`, {
            responseType: 'blob',
            timeout: 30_000,
            ...(refresh ? { params: { v: Date.now() } } : {}),
        });
        if (!response.data.size) throw new Error('The event image response was empty.');
        return URL.createObjectURL(response.data);
    }

    /**
     * Resolve a personal post image URL using authenticated request first, then fallback route.
     */
    async getResolvedPersonalPostImageUrl(postId: string, fallbackImageUrl?: string | null): Promise<string | null> {
        const fallback = typeof fallbackImageUrl === 'string' ? fallbackImageUrl.trim() : '';

        if (postId && postId.trim().length > 0) {
            try {
                const blobResponse = await this.apiClient.get<Blob>(`${this.homeFeedUrl}/posts/${postId}/image`, {
                    responseType: 'blob',
                });
                return URL.createObjectURL(blobResponse.data);
            } catch {
                // Fall back to persisted image URL or direct route.
            }

            if (fallback.length > 0) {
                return fallback;
            }

            return `${window.location.origin}/api/application/participant/home-feed/posts/${postId}/image`;
        }

        return fallback.length > 0 ? fallback : null;
    }

    /**
     * Resolve a specific image's blob URL on a multi-image personal post.
     */
    async getResolvedPersonalPostImageUrlById(postId: string, imageId: string): Promise<string | null> {
        try {
            const blobResponse = await this.apiClient.get<Blob>(`${this.homeFeedUrl}/posts/${postId}/images/${imageId}`, {
                responseType: 'blob',
            });
            return URL.createObjectURL(blobResponse.data);
        } catch {
            return null;
        }
    }

    /**
     * Resolve every image on a multi-image personal post in parallel, skipping any that fail
     * individually rather than failing the whole gallery.
     */
    async getResolvedPersonalPostImageUrls(postId: string, images: { id: string }[]): Promise<(string | null)[]> {
        const results = await Promise.allSettled(images.map((image) => this.getResolvedPersonalPostImageUrlById(postId, image.id)));
        return results.map((result) => (result.status === 'fulfilled' ? result.value : null));
    }

    /**
     * Get reactions for a post or comment
     * Automatically detects whether it's a community or personal post/comment.
     */
    async getReactions(contentType: string, contentId: string, isPersonalPost?: boolean): Promise<IReactionSummary> {
        // If caller explicitly specifies post type, use that
        if (isPersonalPost === true) {
            return this.getPersonalReactions(contentType, contentId);
        }

        if (isPersonalPost === false) {
            const response = await this.apiClient.get(`${this.baseUrl}/reactions/${contentType}/${contentId}`);
            return response.data;
        }

        // Auto-detect: try community endpoint first, fall back to personal if 404
        try {
            const response = await this.apiClient.get(`${this.baseUrl}/reactions/${contentType}/${contentId}`);
            return response.data;
        } catch (error) {
            // If 404, try personal post endpoint
            if (getHttpStatus(error) === 404) {
                return this.getPersonalReactions(contentType, contentId);
            }
            throw error;
        }
    }

    /**
     * Add or update a reaction (Like, Love, Celebrate, Insightful, Support)
     * Automatically detects whether it's a community or personal post/comment.
     */
    async addOrUpdateReaction(contentType: string, contentId: string, reactionType: string, isPersonalPost?: boolean): Promise<IReactionSummary> {
        // If caller explicitly specifies post type, use that
        if (isPersonalPost === true) {
            return this.addOrUpdatePersonalReaction(contentType, contentId, reactionType);
        }

        if (isPersonalPost === false) {
            const response = await this.apiClient.post(`${this.baseUrl}/reactions/${contentType}/${contentId}`, {
                reactionType,
            });
            return response.data;
        }

        // Auto-detect: try community endpoint first, fall back to personal if 404
        try {
            const response = await this.apiClient.post(`${this.baseUrl}/reactions/${contentType}/${contentId}`, {
                reactionType,
            });
            return response.data;
        } catch (error) {
            // If 404, try personal post endpoint
            if (getHttpStatus(error) === 404) {
                return this.addOrUpdatePersonalReaction(contentType, contentId, reactionType);
            }
            throw error;
        }
    }

    /**
     * Remove a reaction
     * Automatically detects whether it's a community or personal post/comment.
     */
    async removeReaction(contentType: string, contentId: string, isPersonalPost?: boolean): Promise<{ status: string }> {
        // If caller explicitly specifies post type, use that
        if (isPersonalPost === true) {
            await this.removePersonalReaction(contentType, contentId);
            return { status: 'removed' };
        }

        if (isPersonalPost === false) {
            const response = await this.apiClient.delete(`${this.baseUrl}/reactions/${contentType}/${contentId}`);
            return response.data;
        }

        // Auto-detect: try community endpoint first, fall back to personal if 404
        try {
            const response = await this.apiClient.delete(`${this.baseUrl}/reactions/${contentType}/${contentId}`);
            return response.data;
        } catch (error) {
            // If 404, try personal post endpoint
            if (getHttpStatus(error) === 404) {
                await this.removePersonalReaction(contentType, contentId);
                return { status: 'removed' };
            }
            throw error;
        }
    }

    /**
     * Report post/comment content for moderation.
     */
    async reportContent(communityId: string, request: IReportContentRequest): Promise<IModerationCase | { status?: string; message?: string }> {
        const response = await this.apiClient.post(`${this.baseUrl}/${communityId}/report`, request);
        return response.data;
    }

    /**
     * Report personal feed content for moderation.
     */
    async reportPersonalContent(postId: string, request: IReportContentRequest): Promise<IModerationCase | { status?: string; message?: string }> {
        const response = await this.apiClient.post(`${this.homeFeedUrl}/posts/${postId}/report`, request);
        return response.data;
    }

    // ─── Personal post endpoints (/participant/home-feed) ─────────────────────

    /**
     * Delete a personal post authored by the current user.
     */
    async deletePersonalPost(postId: string): Promise<void> {
        await this.apiClient.delete(`${this.homeFeedUrl}/posts/${postId}`);
    }

    /**
     * Get comments for a personal post.
     */
    async getPersonalPostComments(postId: string, page = 1, pageSize = 20, options?: CommentRequestOptions): Promise<{ comments: ICommunityComment[]; totalCount: number }> {
        const response = await this.apiClient.get(`${this.homeFeedUrl}/posts/${postId}/comments`, commentRequestConfig(page, pageSize, options));
        return {
            comments: response.data?.comments ?? [],
            totalCount: response.data?.totalCount ?? 0,
        };
    }

    /**
     * Add a comment to a personal post.
     */
    async createPersonalPostComment(postId: string, body: string): Promise<ICommunityComment> {
        const response = await this.apiClient.post(`${this.homeFeedUrl}/posts/${postId}/comments`, { body });
        const data = response.data as ICommunityComment | ICreateCommentResponse;
        return 'comment' in data ? data.comment : data;
    }

    /**
     * Delete a comment on a personal post.
     */
    async deletePersonalPostComment(postId: string, commentId: string): Promise<void> {
        await this.apiClient.delete(`${this.homeFeedUrl}/posts/${postId}/comments/${commentId}`);
    }

    /**
     * Get reactions for a personal post or its comment.
     */
    async getReactionUsers(contentId: string, isPersonalPost: boolean, page = 1, reactionType?: string, contentType: 'Post' | 'Event' = 'Post'): Promise<IReactionSummary> {
        const endpoint = isPersonalPost ? this.homeFeedUrl : this.baseUrl;
        const response = await this.apiClient.get(`${endpoint}/reactions/${contentType}/${contentId}`, {
            params: { includeUsers: true, page, pageSize: 20, reactionType },
        });
        return response.data;
    }

    async getPersonalReactions(contentType: string, contentId: string): Promise<IReactionSummary> {
        const response = await this.apiClient.get(`${this.homeFeedUrl}/reactions/${contentType}/${contentId}`);
        return response.data;
    }

    /**
     * Add or update a reaction on a personal post or comment.
     */
    async addOrUpdatePersonalReaction(contentType: string, contentId: string, reactionType: string): Promise<IReactionSummary> {
        const response = await this.apiClient.post(`${this.homeFeedUrl}/reactions/${contentType}/${contentId}`, { reactionType });
        return response.data;
    }

    /**
     * Cast or toggle a vote on a personal poll option.
     */
    async votePersonalPollOption(postId: string, optionId: string): Promise<IUserPost> {
        const response = await this.apiClient.post(`${this.homeFeedUrl}/posts/${postId}/poll/vote`, { optionId });
        return response.data as IUserPost;
    }

    /**
     * Remove a reaction from a personal post or comment.
     */
    async removePersonalReaction(contentType: string, contentId: string): Promise<void> {
        await this.apiClient.delete(`${this.homeFeedUrl}/reactions/${contentType}/${contentId}`);
    }
}
